import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn, Input, Select } from "../components/UI";
import { toast } from "sonner";
import { Scale, Plus, Trash2, Loader2, ExternalLink, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ClaimsVault() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ claim: "", category: "policy", source: "", source_url: "" });

  const load = () => api.get("/political-os/claims").then(r => setData(r.data)).catch(() => setData({ claims: [] }));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.claim.trim()) return toast.error("Enter the claim text");
    try { await api.post("/political-os/claims", form); toast.success("Claim saved"); setForm({ claim: "", category: "policy", source: "", source_url: "" }); load(); }
    catch (e) { toast.error(errMsg(e, "Could not save claim")); }
  };
  const remove = async (id) => { try { await api.delete(`/political-os/claims/${id}`); load(); } catch (e) { toast.error(errMsg(e)); } };

  if (!data) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-cyan-300" /></div>;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="claims-vault-page">
      <PageHeader eyebrow="Compliance" title="Claims & Source Vault"
        subtitle="Every factual claim used in political creative should carry an approved source — claims without documentation are flagged." />
      <Card data-testid="add-claim-card">
        <div className="space-y-3">
          <Input label="Claim" value={form.claim} onChange={e => setForm(f => ({ ...f, claim: e.target.value }))}
            placeholder='"Property taxes increased 12% under the incumbent"' data-testid="claim-input" />
          <div className="flex flex-wrap gap-3 items-end">
            <Select label="Category" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
              {["policy", "record", "finance", "endorsement", "biography", "statistic"].map(c => <option key={c}>{c}</option>)}
            </Select>
            <Input label="Source" value={form.source} onChange={e => setForm(f => ({ ...f, source: e.target.value }))} placeholder="County appraisal district report" className="flex-1 min-w-[180px]" />
            <Input label="Source URL" value={form.source_url} onChange={e => setForm(f => ({ ...f, source_url: e.target.value }))} placeholder="https://…" className="flex-1 min-w-[180px]" data-testid="claim-source-url" />
            <Btn onClick={add} data-testid="add-claim-btn"><Plus className="w-4 h-4" /> Save claim</Btn>
          </div>
        </div>
      </Card>
      <Card padded={false} data-testid="claims-list-card">
        <div className="divide-y divide-white/5">
          {data.claims.map(c => (
            <div key={c.id} className="px-5 py-4 flex items-start gap-4" data-testid={`claim-${c.id}`}>
              {c.status === "approved" ? <CheckCircle2 className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" /> : <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="text-sm text-white">{c.claim}</div>
                <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                  <span className="uppercase tracking-wider">{c.category}</span>
                  <span className={c.status === "approved" ? "text-emerald-300" : "text-amber-300"}>{c.status === "approved" ? "Claim verified · source attached" : "Source required"}</span>
                  {c.source && <span>{c.source}</span>}
                  {c.source_url && <a href={c.source_url} target="_blank" rel="noreferrer" className="text-cyan-300 inline-flex items-center gap-0.5 hover:underline">source <ExternalLink className="w-3 h-3" /></a>}
                </div>
              </div>
              <button onClick={() => remove(c.id)} className="text-slate-600 hover:text-red-400 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {data.claims.length === 0 && <div className="px-5 py-6 text-sm text-slate-500">No claims yet — document your first campaign claim above.</div>}
        </div>
      </Card>
    </div>
  );
}
